import { useEffect, useState } from 'react';
import { getGroups } from '../api/firebase/firestore/group/read';
import type { GroupType } from '../types/group';

/**
 * 企業のグループ一覧とグループID→名のマップを取得する共通フック（#2804）
 * パターン作成・一覧・担当者編集などグループ情報を扱う画面で共用し、取得処理の重複を避ける。
 * - groups: 有効なグループ一覧（作成日昇順）
 * - groupNameMap: グループID→グループ名（バッジ・チップ表示用）
 * - loading: 取得中フラグ
 */
export const useCompanyGroups = (
  companyId: string | null | undefined,
): {
  groups: GroupType[];
  groupNameMap: Record<string, string>;
  loading: boolean;
} => {
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [groupNameMap, setGroupNameMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!companyId) {
      setGroups([]);
      setGroupNameMap({});
      return;
    }
    let active = true;
    setLoading(true);
    void getGroups(companyId)
      .then(fetched => {
        if (!active) return;
        setGroups(fetched);
        setGroupNameMap(
          fetched.reduce<Record<string, string>>((acc, g) => {
            acc[g.id] = g.name;
            return acc;
          }, {}),
        );
      })
      .catch(() => {
        if (!active) return;
        setGroups([]);
        setGroupNameMap({});
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  return { groups, groupNameMap, loading };
};
